import React, { useEffect } from 'react';
import { Platform } from 'react-native';

interface WebAppWrapperProps {
  children: React.ReactNode;
}

/**
 * Web-specific wrapper component that handles web-only functionality
 * Includes SEO, analytics, and enhanced navigation features
 */
export const WebAppWrapper: React.FC<WebAppWrapperProps> = ({ children }) => {
  // Only activate on web
  if (Platform.OS !== 'web') {
    return <>{children}</>;
  }

  return <WebAppContent>{children}</WebAppContent>;
};

const WebAppContent: React.FC<WebAppWrapperProps> = ({ children }) => {
  // Web-specific setup without navigation hooks (since we're outside navigation context)
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') {
      return;
    }

    // Set default SEO meta tags
    document.title = 'Coffeeplexity - Your Coffee Brewing Companion';

    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute(
      'content',
      'Discover amazing coffee recipes, learn brewing techniques, and connect with coffee creators worldwide.'
    );

    // Update meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute(
      'content',
      'coffee, brewing, recipes, barista, equipment'
    );

    // Add Open Graph meta tags
    const ogTags = [
      {
        property: 'og:title',
        content: 'Coffeeplexity - Your Coffee Brewing Companion',
      },
      {
        property: 'og:description',
        content:
          'Discover amazing coffee recipes, learn brewing techniques, and connect with coffee creators worldwide.',
      },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: window.location.href },
    ];

    ogTags.forEach(({ property, content }) => {
      let ogTag = document.querySelector(`meta[property="${property}"]`);
      if (!ogTag) {
        ogTag = document.createElement('meta');
        ogTag.setAttribute('property', property);
        document.head.appendChild(ogTag);
      }
      ogTag.setAttribute('content', content);
    });

    // Add structured data
    let structuredDataScript = document.querySelector('#structured-data');
    if (!structuredDataScript) {
      structuredDataScript = document.createElement('script');
      structuredDataScript.id = 'structured-data';
      structuredDataScript.type = 'application/ld+json';
      document.head.appendChild(structuredDataScript);
    }

    structuredDataScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: 'Coffeeplexity',
      description: 'Coffee brewing recipes and community platform',
      url: 'https://app.coffeeplexity.com',
      applicationCategory: 'Food & Drink',
    });
  }, []);

  return <>{children}</>;
};
